import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';

import { brandPrimaryHex, type BrandKit, type Project } from '@/types';

/**
 * homeMeta — 首頁 dashboard 卡片的純函式派生(從 `homePageSections.tsx` 抽出)。
 * -----------------------------------------------------------------------------
 * 這裡只放「無 JSX / 無 React hook」的純計算,方便 co-located unit test 鎖邊界:
 *
 *   - `projectCoverStyle`  : project cover 配色(projectId hash mod 4 + brand kit primary override)
 *   - `projectMetaLine`    : 「· 編輯於 N 小時前」相對時間文案(走 i18n,t 注入)
 *   - `greetingKeyForHour` : 時段問候 i18n key(05/11/18 邊界 + 跨 0 點走晚安)
 *
 * 行為與原內聯版逐字等價;顏色一律走 `theme.css` CSS token,不寫死 hex。
 */

/**
 * 時段問候 i18n key 派生:05-10 早安 / 11-17 午安 / 18-04 晚安(跨 0 點走晚安)。
 *
 * @param hour - `Date.getHours()`(0-23)。
 * @returns `'greeting.morning' | 'greeting.afternoon' | 'greeting.evening'`。
 */
export function greetingKeyForHour(
  hour: number,
): 'greeting.morning' | 'greeting.afternoon' | 'greeting.evening' {
  return hour >= 5 && hour < 11
    ? 'greeting.morning'
    : hour >= 11 && hour < 18
      ? 'greeting.afternoon'
      : 'greeting.evening';
}

/**
 * Deterministic cover color via projectId hash;brand kit primary 優先 override。
 *
 * R245 起這是**未設封面(`cover.kind === 'none'`)時的 fallback**,不再是唯一來源
 * ——真封面走 `project.cover`,由 `CoverSurface` 渲染。BE docs/92 §4 明確要求保留
 * 這條漸層當 fallback。
 */
export function projectCoverFallback(project: Project, brandKit: BrandKit | null): string {
  // ⚠️ `colors.primary` 是 **CSV 字串**(BE 2026-08-17)—— 整串內插進漸層會生出
  // 一條停點錯亂的假漸層(`#0D9488,#11111140, #0D9488,#111111`),非 HEX 的存量值
  // 更會讓整條宣告作廢。取第一個合法 HEX,查無就走下面的 projectId 雜湊色。
  const c = brandPrimaryHex(brandKit?.colors);
  if (c) {
    return `linear-gradient(135deg, ${c}40, ${c})`;
  }
  const presets = [
    'linear-gradient(135deg, var(--denim-500), var(--denim-750))', // denim blue
    'linear-gradient(135deg, var(--amber-400), var(--amber-700))', // food amber

    'linear-gradient(135deg, var(--danger-300), var(--danger-600))', // warm red
    'linear-gradient(135deg, var(--info-300), var(--info-800))', // cool blue
  ];
  let h = 0;
  for (let i = 0; i < project.projectId.length; i++) {
    h = ((h << 5) - h + project.projectId.charCodeAt(i)) | 0;
  }
  return presets[Math.abs(h) % presets.length];
}

/** `projectCoverFallback` 的 CSSProperties 包裝(既有呼叫端與測試沿用)。 */
export function projectCoverStyle(
  project: Project,
  brandKit: BrandKit | null,
): CSSProperties {
  return { background: projectCoverFallback(project, brandKit) };
}

/**
 * 「· 編輯於 2 小時前」— 時間從 updatedDatetime / createdDatetime 派生(asset count BE TODO)。
 * 相對時間走 i18n `relativeTime.*`({{count}} 插值),en/ja 不再永遠顯中文。
 */
export function projectMetaLine(project: Project, t: TFunction<'home'>): string {
  const editedAt = project.updatedDatetime ?? project.createdDatetime;
  if (!editedAt) return '—';
  const date = new Date(editedAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffH = Math.floor(diffMs / 3_600_000);
  const diffD = Math.floor(diffMs / 86_400_000);
  let rel = '';
  if (diffH < 1) rel = t('relativeTime.justNow');
  else if (diffH < 24) rel = t('relativeTime.hoursAgo', { count: diffH });
  else if (diffD < 7) rel = t('relativeTime.daysAgo', { count: diffD });
  else rel = t('relativeTime.weeksAgo', { count: Math.floor(diffD / 7) });
  return `· ${rel}`;
}
